import React, {
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Box, Collapse, SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { getFeedbackUser } from '@/hooks/apiHooks';
import usePopper from '@/hooks/usePopper';
import {
  FoldButton,
  LikeItem,
  LikeList,
  LikeSummary,
} from '@/components/Board/BoardDetail/Feedback/BoardDetailLikes/styles';
import classNames from 'classnames';
import { imgUrlChk } from '@/utils/function';
import ImageWithErrorHandling from '@/components/ImageWithErrorHandling';
import LikeCircleOffIcon from '@/public/icons/like_circle_off.svg';
import LikeCircleOnIcon from '@/public/icons/like_circle_on.svg';
import AngleBracketIcon from '@/public/icons/angle_bracket.svg';
import ProfileCardPopper from '@/components/@IntranetLibrary/ProfileCard/ProfileCardPopper';
import { useTranslation } from 'react-i18next';

type BoardDetailLikesProps = {
  documentId: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
};
export type BoardDetailLikesRef = {
  refresh: () => void;
  setIsOpen: (isOpen: boolean) => void;
};
export const BoardDetailLikesID = 'BoardDetailLikes';

const BoardDetailLikes = forwardRef<BoardDetailLikesRef, BoardDetailLikesProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const { documentId, disabled } = props;
    const { data, isLoading, mutate } = getFeedbackUser(documentId);

    const [isOpen, setIsOpen] = useState(false);
    const [summaryText, setSummaryText] = useState<string | ReactNode>(
      t('불러오는 중...'),
    );

    const [passportId, setPassportId] = useState<string>('');

    const { anchorEl, openPopper, closePopper } = usePopper<HTMLButtonElement>(
      (el) => {
        const userId = el.dataset?.['userId'];
        setPassportId(userId ?? '');
      },
    );

    useEffect(() => {
      if (isLoading) setSummaryText(t('불러오는 중...'));
      else if (!Array.isArray(data) || data.length === 0) {
        setSummaryText(
          <>
            게시물에 첫번째로 <strong>좋아요</strong>를 눌러보세요.
          </>,
        );
      } else {
        setSummaryText(
          <>
            <strong>{data.length}</strong>명의 프프인이 이 게시물을 좋아합니다.
          </>,
        );
      }
    }, [isLoading, data]);

    useImperativeHandle(ref, () => ({
      refresh: mutate,
      setIsOpen,
    }));

    return (
      <Box id={BoardDetailLikesID}>
        <LikeSummary
          disabled={disabled || data?.length === 0}
          className={classNames({ open: isOpen })}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className={'profile-image-container'}>
            {(Array.isArray(data) ? data.slice(0, 4) : []).map((item) => (
              <ImageWithErrorHandling
                key={item.passportId}
                src={imgUrlChk(item.profileImageUrl)}
                width={32}
                height={32}
                alt={`${item.name} Image`}
                className={'profile-image'}
              />
            ))}
            {isOpen ? <LikeCircleOnIcon /> : <LikeCircleOffIcon />}
          </div>
          <p>{summaryText}</p>
          <AngleBracketIcon className="angle-bracket-icon" />
        </LikeSummary>
        {Array.isArray(data) && data.length > 0 && (
          <Collapse in={isOpen} timeout={'auto'} unmountOnExit>
            <LikeList sx={{ marginTop: '20px' }}>
              {data?.map((item) => (
                <LikeItem
                  key={item.passportId}
                  data-user-id={item.passportId}
                  disabled={disabled}
                  onClick={openPopper}
                >
                  <ImageWithErrorHandling
                    src={imgUrlChk(item.profileImageUrl)}
                    width={32}
                    height={32}
                    alt={`${item.name} Image`}
                    className={'profile-image'}
                  />
                  <span>
                    {item.name} {item.positionName}
                  </span>
                </LikeItem>
              ))}
            </LikeList>
            <Box sx={{ marginTop: '20px' }}>
              <FoldButton onClick={() => setIsOpen(false)}>
                좋아요 내역 숨기기
              </FoldButton>
            </Box>
          </Collapse>
        )}
        <ProfileCardPopper
          passportId={passportId}
          anchorEl={anchorEl}
          onClose={closePopper}
        />
      </Box>
    );
  },
);
BoardDetailLikes.displayName = 'BoardDetailLikes';

export default BoardDetailLikes;
